"use client";
import React, { useEffect, useState } from "react";
import "./IndexPageLayout.css";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import { Menu, Sun, Moon, Lock } from "lucide-react";
import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";
import useEmployeeDataStore from "../../Zustand/Store/useEmployeeDataStore";
import { routes } from "../../Configurations/PermissionConfigrations";
import NotAllowed from "../../Exceptions/NotAllowed";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import useInternDataStore from "../../Zustand/Store/useInternDataStore";
import SideBar from "../../Assets/Static/SideBar";
import { CORE_FRONTEND_PERMISSIONS_DISABLED } from "../../Configurations/corePermissionsFlags";
import { coreHasPermission } from "../../Configurations/corePermissionUtils";
import {
  clearLocalAuthSession,
  getStoredLocalAuthUser,
  isLocalAuthToken,
} from "../../Configurations/coreLocalAuth";
import { buildCoreSessionUser } from "../../Configurations/coreSession";
import { createEmsTheme } from "../../theme/emsMuiTheme";
import CoreBrand from "../../Components/CoreBrand";

function userInitials(name) {
  const parts = String(name || "U")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "U";
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

export default function RouteProtector({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("ems_dark_mode") === "true";
    } catch {
      return false;
    }
  });
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("ems-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const { resetData } = useEmployeeDataStore();
  const { resetData: clearData } = useInternDataStore();
  const { Permission, setPermission } =
    usePermissionDataStore();

  const isMobile = useMediaQuery("(max-width:768px)", { noSsr: true });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("ems_dark_mode", String(darkMode));
    } catch {
      // Ignore storage failures (private mode / blocked storage).
    }
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const theme = React.useMemo(
    () => createEmsTheme(darkMode ? "dark" : "light"),
    [darkMode]
  );

  const navigate = useNavigate();
  const location = useLocation();

  const [activeMenu, setActiveMenu] = useState(null);

  const toggleMenu = (m) => {
    setActiveMenu((cur) => (cur === m ? null : m));
  };
  const closeMenu = () => setActiveMenu(null);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed((c) => {
        const next = !c;
        try {
          localStorage.setItem("ems-collapsed", next ? "1" : "0");
        } catch {
          // Ignore storage failures.
        }
        return next;
      });
    }
  };

  useEffect(() => {
    if (!activeMenu) return;
    const isInsideOpenMenu = (target) => {
      if (!(target instanceof Element)) return false;
      if (target.closest(".ems-tb-pop")) return true;
      if (activeMenu === "user" && target.closest(".ems-user-chip")) return true;
      return false;
    };
    const onDown = (e) => {
      if (!isInsideOpenMenu(e.target)) setActiveMenu(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setActiveMenu(null);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [activeMenu]);

  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  const { logout, userData, setAccess, login } = useAuthStore();
  const access = userData?.role || "Admin";
  const [loadPermission, setLoadPermission] = useState(
    !CORE_FRONTEND_PERMISSIONS_DISABLED
  );

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0891B2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");

          if (!isLocalAuthToken(token)) {
            await axios.get(`${MAIN_URL}/api/auth/logout`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });
          }

          logout();
          clearLocalAuthSession();

          Swal.fire("Logged out!", "You have been logged out.", "success");
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
          Swal.fire("Error", "Logout failed. Please try again.", "error");
        }
      }
    });
  };

  const [OpenTCode, setOpenTCode] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "/") {
        setOpenTCode(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const [isAllowed, setisAllowed] = useState(true);

  useEffect(() => {
    const currentPath = location.pathname;
    localStorage.setItem("lastVisitedRoute", currentPath);

    if (CORE_FRONTEND_PERMISSIONS_DISABLED) {
      setisAllowed(true);
      return;
    }

    if (currentPath && Permission?.length > 0) {
      const basePath = currentPath.includes("/edit")
        ? `${currentPath.split("/edit")[0]}/edit`
        : currentPath;

      const matchedRoute = routes.find((item) => item.name === basePath);
      if (!matchedRoute) {
        setisAllowed(true);
      } else if (
        matchedRoute.action &&
        coreHasPermission(Permission, matchedRoute?.action)
      ) {
        setisAllowed(true);
      } else {
        setisAllowed(false);
      }
    }
  }, [location, Permission]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    if (isLocalAuthToken(token)) {
      const user = getStoredLocalAuthUser();
      setAccess(user?.role?.system_role_name);
      login(buildCoreSessionUser(user) || user);
      return;
    }

    axios
      .get(`${MAIN_URL}/api/auth/token-login`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { user } = response.data;

        setAccess(user?.role?.system_role_name);
        login(buildCoreSessionUser(user));
      })
      .catch((error) => {
        navigate("/login");
      });
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    if (
      currentPath === "/organization/employee/employee-details/add" ||
      currentPath === "/organization/employee/employee-details"
    ) {
      resetData();
    }
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (
      currentPath === "/organization/intern/intern-details/add" ||
      currentPath === "/organization/intern/intern-details"
    ) {
      clearData();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (CORE_FRONTEND_PERMISSIONS_DISABLED) {
      setLoadPermission(false);
      return;
    }

    const checkPermission = async () => {
      setLoadPermission(true);

      try {
        const res = await axios.get(
          `${MAIN_URL}/api/user/${userData?.metadata?.[0]?.organization_user_id}/check-permission-access`
        );

        const allowed = res?.data?.allowed_actions || [];
        setPermission(allowed);
      } catch (error) {
        console.error("Permission check error:", error);
        setPermission([]);
      } finally {
        setLoadPermission(false);
      }
    };

    const orgUserId = userData?.metadata?.[0]?.organization_user_id;
    if (orgUserId) {
      checkPermission();
    } else {
      setLoadPermission(false);
    }
  }, [userData?.metadata?.[0]?.organization_user_id]);

  if (loadPermission) {
    return (
      <div className="ems-shell" style={{ alignItems: "center", justifyContent: "center" }}>
        <div className="ems-card" style={{ padding: 32, textAlign: "center", minWidth: 240 }}>
          <div className="ems-fm-loading">Loading…</div>
          <p className="ems-page-sub" style={{ marginTop: 8 }}>Please wait.</p>
        </div>
      </div>
    );
  }

  const currentPath = location.pathname;

  if (access === "Admin" && currentPath === "/sidebar-option-page-1") {
    return <Navigate to="/" replace />;
  }

  if (!isAllowed) {
    return <NotAllowed />;
  }

  const roleName =
    access?.system_role_name ||
    (typeof access === "string" ? access : "") ||
    userData?.role?.system_role_name ||
    "";

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="ems-shell">
        {activeMenu && (
          <div
            className="ems-tb-pop-backdrop"
            aria-hidden="true"
            onPointerDown={(e) => {
              e.preventDefault();
              closeMenu();
            }}
          />
        )}

        <header className="ems-topbar">
          <div className="ems-topbar-lead">
            <button className="ems-icon-btn" onClick={handleDrawerToggle} aria-label="Toggle sidebar">
              <Menu size={21} />
            </button>
            <CoreBrand onClick={() => navigate("/")} />
          </div>

          <div className="ems-topbar-spacer" />

          <div className="ems-topbar-tools">
            <button
              className="ems-icon-btn"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? "Light mode" : "Dark mode"}
            >
              {darkMode ? <Sun size={19} /> : <Moon size={19} />}
            </button>

            <div className="ems-tb-anchor">
              <button
                className={`ems-user-chip${activeMenu === "user" ? " on" : ""}`}
                onClick={() => toggleMenu("user")}
                aria-label="User menu"
              >
                <span className="ems-user-name">{userData?.full_name || "User"}</span>
                <span className="ems-avatar">{userInitials(userData?.full_name)}</span>
              </button>
              {activeMenu === "user" && (
                <div className="ems-tb-pop ems-tb-pop-menu" style={{ minWidth: 260 }}>
                  <div className="ems-tb-user-head">
                    <span className="ems-avatar" style={{ width: 44, height: 44, fontSize: 16 }}>
                      {userInitials(userData?.full_name)}
                    </span>
                    <div className="ems-tb-user-meta">
                      <span className="ems-tb-user-name">{userData?.full_name || "User"}</span>
                      <span className="ems-tb-user-mail">{userData?.email || ""}</span>
                      {roleName ? <span className="ems-tb-user-role">{roleName}</span> : null}
                    </div>
                  </div>
                  <div className="ems-tb-divider" />
                  {(access?.system_role_name === "Admin" || roleName === "Admin") && (
                    <button
                      className="ems-tb-item"
                      onClick={() => {
                        closeMenu();
                        navigate("/my-profile");
                      }}
                    >
                      Profile
                    </button>
                  )}
                  {coreHasPermission(Permission, "USER_RESET_PASSWORD") && (
                    <button
                      className="ems-tb-item"
                      onClick={() => {
                        closeMenu();
                        navigate("/settings/change-password");
                      }}
                    >
                      <Lock size={17} />
                      Change Password
                    </button>
                  )}
                  <div className="ems-tb-divider" />
                  <button
                    className="ems-tb-item danger"
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="ems-body">
          {isMobile && mobileOpen && (
            <div className="ems-sidebar-overlay" onClick={() => setMobileOpen(false)} />
          )}
          <aside
            className={[
              "ems-sidebar",
              collapsed && !isMobile ? "ems-collapsed" : "",
              isMobile && mobileOpen ? "mobile-open" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <SideBar
              Permission={Permission}
              collapsed={collapsed && !isMobile}
              onRequestExpand={() => {
                setCollapsed(false);
                try {
                  localStorage.setItem("ems-collapsed", "0");
                } catch {
                  // Ignore storage failures.
                }
              }}
            />
          </aside>
          <main className="ems-content">
            <div className="ems-content-inner">{children}</div>
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}
